// oxlint-disable no-console -- console.warn in catch blocks is intentional for localStorage error handling
import * as React from 'react'
import { localStore } from 'utils/storage'

export const PRODUCT_HISTORY_LOCALSTORAGE_KEY_PREFIX = 'cf-product-history'
export const MAX_HISTORY_ITEMS = 12

const getProductHistoryKey = (userId: string): string => {
  return `${PRODUCT_HISTORY_LOCALSTORAGE_KEY_PREFIX}-${userId}`
}

export const saveProductIdToLocalStorage = (userId: string, productId: string): void => {
  if (!productId || !userId) {
    return
  }

  try {
    const storageKey = getProductHistoryKey(userId)
    const storedHistory = localStore.getString(storageKey)
    let productHistory: string[] = []

    // Parse the stored history or default to empty array
    try {
      if (storedHistory) {
        const parsed = JSON.parse(storedHistory)
        productHistory = Array.isArray(parsed) ? parsed : []
      }
    } catch (parseError) {
      console.warn('Failed to parse product history from localStorage:', parseError)
    }

    const productIndex = productHistory.findIndex((product: string) => product === productId)

    if (productIndex !== -1) {
      productHistory.splice(productIndex, 1)
    }

    productHistory.unshift(productId)

    if (productHistory.length > MAX_HISTORY_ITEMS) {
      productHistory.pop()
    }

    localStore.setJSON(storageKey, productHistory)
  } catch (error) {
    console.warn('Failed to save product history to localStorage:', error)
  }
}

const getProductHistoryFromLocalStorage = (userId: string): string[] => {
  if (!userId) {
    return []
  }
  try {
    const storageKey = getProductHistoryKey(userId)
    const storedHistory = localStore.getString(storageKey)
    if (storedHistory) {
      const parsed = JSON.parse(storedHistory)
      return Array.isArray(parsed) ? parsed : []
    }
    return []
  } catch (error) {
    console.warn('Failed to get product history from localStorage:', error)
    return []
  }
}

export const useGetProductHistory = (userId: string): string[] => {
  const [history, setHistory] = React.useState<string[]>(getProductHistoryFromLocalStorage(userId))
  React.useEffect(() => {
    setHistory(getProductHistoryFromLocalStorage(userId))
  }, [userId])
  return history
}

/**
 * This can be removed once all products are rendered from cf-web instead of Wordpress
 * @param productIdQueryParam This param will only be present in the Next.JS PDP
 */
export const useStoreProductHistory = (userId: string, productIdPDP?: string): void => {
  React.useEffect(() => {
    const productDocumentQuery = 'form input[name=product_id]'

    let observer: MutationObserver | null = null

    const findProductId = () => {
      const productIdWP = document.querySelector(productDocumentQuery)?.getAttribute('value') ?? ''

      if (productIdWP) {
        // If we have a WordPress product ID, use it
        saveProductIdToLocalStorage(userId, productIdWP)
        if (observer) {
          observer.disconnect()
          observer = null
        }
      } else if (productIdPDP) {
        // Only use Next.js product ID if WordPress product ID is not available
        saveProductIdToLocalStorage(userId, productIdPDP)
      }
    }

    // Try to find the element immediately first
    findProductId()

    // Set up the observer to watch for DOM changes if we don't have a WordPress product ID yet
    if (!document.querySelector(productDocumentQuery)) {
      observer = new MutationObserver((_, obs) => {
        const productIdWP =
          document.querySelector(productDocumentQuery)?.getAttribute('value') ?? ''
        if (productIdWP) {
          findProductId()
          obs.disconnect()
        }
      })

      // Start observing the document with the configured parameters
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
    }

    // Cleanup function to disconnect the observer
    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [userId, productIdPDP])
}
